"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { sumNightCounts } from "@/features/event-details-rooms/_logic/rooms";
import type { RoomType } from "@/lib/utils/accommodations";
import { formatDateWithWeekday } from "@/lib/utils/formatting";
import { formatRoomCount } from "@/lib/utils/room-count";
import { cn } from "@/lib/utils/index";
import { X } from "lucide-react";
import { memo } from "react";
import type { RoomTypeCounts, ShoulderSegment, StayNight } from "../../../_types/wizard-types";

interface GuestroomRowProps {
  /** This row's night, segment, and label. */
  night: StayNight;
  /** This night's per-room-type counts, or `undefined` if unset. */
  counts: RoomTypeCounts | undefined;
  /** Room types currently shown as columns. */
  visibleColumns: RoomType[];
  /** Called with `(night, roomType, rawInputValue)` on any cell edit. */
  onCellChange: (night: StayNight, roomType: RoomType, raw: string) => void;
  /** Set only on the outermost pre/post night: removes that night. */
  onRemove?: (segment: ShoulderSegment) => void;
}

/**
 * One night's row in the guestrooms grid. Memoized so a keystroke in one night's cell doesn't
 * re-render every other row — `counts` is that night's own slice of the breakdown, which keeps its
 * object identity across an edit to a different night, and `onCellChange` is a stable callback, so
 * unrelated rows correctly bail out. Pre/post nights render muted so the peak "Night 1…N"
 * numbering reads clearly, and the outermost one gets a remove button.
 *
 * @param props - The night, its counts, the visible columns, and the edit/remove handlers.
 * @returns The table row.
 */
export const GuestroomRow = memo(function GuestroomRow({
  night,
  counts,
  visibleColumns,
  onCellChange,
  onRemove,
}: GuestroomRowProps) {
  // Derived values
  const total = sumNightCounts(counts);
  const weekdayLabel = formatDateWithWeekday(night.date);
  const isShoulder = night.segment !== "peak";
  const removableSegment = night.segment === "peak" ? null : night.segment;

  // Render
  return (
    <tr className={cn("transition-colors hover:bg-indigo-50/30", isShoulder && "bg-gray-50/60")}>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <span
              className={cn("text-sm font-medium whitespace-nowrap", isShoulder ? "text-gray-600" : "text-gray-900")}
            >
              {weekdayLabel}
            </span>
            <span
              data-testid="guestroom-night-label"
              className={cn(
                "text-xs font-medium whitespace-nowrap",
                isShoulder ? "italic text-gray-600" : "text-gray-400"
              )}
            >
              {night.label}
            </span>
          </div>
          {onRemove && removableSegment ? (
            <FetchButton
              variant="ghost"
              color="neutral"
              size="sm"
              iconOnly
              leftIcon={<X className="size-3.5" aria-hidden />}
              onClick={() => onRemove(removableSegment)}
              aria-label={`Remove ${night.label.toLowerCase()} (${weekdayLabel})`}
            />
          ) : null}
        </div>
      </td>
      {visibleColumns.map((type) => (
        <td key={type} className="px-3 py-2.5 align-middle">
          <div className="mx-auto w-16">
            <FetchInput
              type="number"
              hideStepper
              min={0}
              value={formatRoomCount(counts?.[type])}
              onChange={(e) => onCellChange(night, type, e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              aria-label={`${type} rooms for ${weekdayLabel} (${night.label.toLowerCase()})`}
              className="text-center tabular-nums"
            />
          </div>
        </td>
      ))}
      <td className="px-3 py-2.5 text-center text-sm font-semibold tabular-nums text-gray-900">{total}</td>
    </tr>
  );
});
